// src/components/winback/InactiveCustomersTable.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faSync, faRocket, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useInactiveCustomers } from '../../hooks/useInactiveCustomers';
import { get } from '../../helpers/apiHelper';
import UrgencyBadge from './UrgencyBadge';
import SegmentBadge from './SegmentBadge';
import SendWinbackModal from './SendWinbackModal';
import winbackHelper from '../../helpers/winbackHelper';
import Swal from 'sweetalert2';

// Estilos personalizados para react-select con z-index alto
const selectStyles = {
    menu: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    })
};

const InactiveCustomersTable = ({ marcaId }) => {
    const {
        customers,
        loading,
        refresh,
        filters,
        filterByUrgency,
        filterBySegment,
        clearFilters,
        error
    } = useInactiveCustomers(marcaId);

    const [hasAnyClients, setHasAnyClients] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [sendingBatch, setSendingBatch] = useState(false);

    // Verificar si hay clientes en la marca
    useEffect(() => {
        const checkClients = async () => {
            if (!marcaId) return;
            try {
                const clientes = await get(`marcas/${marcaId}/clientes`);
                setHasAnyClients(clientes && clientes.length > 0);
            } catch (err) {
                console.error('Error checking clients:', err);
                setHasAnyClients(null); // No sabemos, así que no mostramos el empty state especial
            }
        };
        checkClients();
    }, [marcaId]);

    // Opciones para filtros
    const urgencyOptions = [
        { value: null, label: 'Todas las urgencias' },
        { value: 'high', label: '🔴 Alta' },
        { value: 'medium', label: '🟡 Media' },
        { value: 'low', label: '🟢 Baja' }
    ];

    const segmentOptions = [
        { value: null, label: 'Todos los segmentos' },
        { value: 'Campeones', label: 'Campeones' },
        { value: 'Clientes Fieles', label: 'Clientes Fieles' },
        { value: 'En Riesgo', label: 'En Riesgo' },
        { value: 'No Pueden Perderse', label: 'No Pueden Perderse' },
        { value: 'A Punto de Dormir', label: 'A Punto de Dormir' },
        { value: 'Perdidos', label: 'Perdidos' },
        { value: 'Sin Compras', label: 'Sin Compras' }
    ];

    // Paginación
    const paginatedCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return customers.slice(startIndex, endIndex);
    }, [customers, currentPage, pageSize]);

    const totalPages = Math.ceil(customers.length / pageSize);

    // Handlers
    const handleSendClick = (customer) => {
        setSelectedClient(customer);
        setShowSendModal(true);
    };

    const handleSendSuccess = () => {
        setShowSendModal(false);
        setSelectedClient(null);
        refresh();
        Swal.fire({
            title: '¡Éxito!',
            text: 'WinBack enviado correctamente',
            icon: 'success',
            confirmButtonText: 'Aceptar'
        });
    };

    const handleRefresh = () => {
        refresh();
        Swal.fire({
            title: 'Actualizado',
            text: 'Lista de clientes actualizada',
            icon: 'info',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleSendBatchHigh = async () => {
        // Filtrar solo clientes con urgencia HIGH
        const highUrgencyCustomers = customers.filter(c => c.urgency === 'high');

        if (highUrgencyCustomers.length === 0) {
            Swal.fire({
                title: 'Sin clientes',
                text: 'No hay clientes con urgencia alta para enviar',
                icon: 'info',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Confirmar acción
        const result = await Swal.fire({
            title: '¿Enviar WinBack a todos los HIGH?',
            html: `Se enviarán <strong>${highUrgencyCustomers.length}</strong> WinBacks a clientes con urgencia alta.<br><br>Esta acción puede tardar unos momentos.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, enviar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#8B5CF6',
            cancelButtonColor: '#6B7280'
        });

        if (!result.isConfirmed) return;

        setSendingBatch(true);
        try {
            const clienteIds = highUrgencyCustomers.map(c => c.clienteId);
            const response = await winbackHelper.sendBatch(clienteIds, { channel: 'email' });

            // Contar éxitos y errores
            const successCount = response.results.filter(r => r.success).length;
            const errorCount = response.results.filter(r => !r.success).length;

            Swal.fire({
                title: '¡Envío completado!',
                html: `
                    <div class="text-left">
                        <p><strong>Enviados exitosamente:</strong> ${successCount}</p>
                        ${errorCount > 0 ? `<p class="text-red-600"><strong>Errores:</strong> ${errorCount}</p>` : ''}
                    </div>
                `,
                icon: successCount > 0 ? 'success' : 'error',
                confirmButtonText: 'Aceptar'
            });

            // Refrescar lista
            refresh();
        } catch (error) {
            console.error('Error sending batch:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo enviar el lote de WinBacks',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setSendingBatch(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <div className="spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full text-purple-600 mb-4"></div>
                        <p className="text-gray-600">Cargando clientes inactivos...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Empty state - Solo cuando NO existen clientes en absoluto
    if (customers.length === 0 && !filters.urgency && !filters.segment && hasAnyClients === false) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-12">
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FontAwesomeIcon icon={faUserPlus} className="text-purple-600 text-2xl" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Agrega tus primeros clientes
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Para empezar a usar WinBack y recuperar clientes perdidos, necesitas agregar clientes a tu marca
                    </p>
                </div>

                <div className="text-center">
                    <button
                        onClick={() => window.location.href = '/clientes/crear'}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all shadow-lg hover:shadow-xl"
                    >
                        <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                        Agregar Primer Cliente
                    </button>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                        💡 Una vez que agregues clientes y registres sus visitas o compras, podremos detectar automáticamente quién está inactivo y necesita un WinBack.
                    </p>
                </div>
            </div>
        );
    }

    // Empty state normal cuando hay clientes pero no inactivos
    if (customers.length === 0 && !filters.urgency && !filters.segment && hasAnyClients !== false) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faPaperPlane} className="text-green-600 text-2xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    ¡Excelente! No hay clientes inactivos
                </h3>
                <p className="text-gray-500">
                    Todos tus clientes están activos y comprometidos
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header con filtros */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Clientes Inactivos
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {customers.length} cliente{customers.length !== 1 ? 's' : ''} encontrado{customers.length !== 1 ? 's' : ''}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Filtro por Urgencia */}
                        <div className="w-full sm:w-48">
                            <Select
                                options={urgencyOptions}
                                value={urgencyOptions.find(opt => opt.value === filters.urgency)}
                                onChange={(option) => filterByUrgency(option.value)}
                                placeholder="Filtrar por urgencia"
                                className="text-sm"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>

                        {/* Filtro por Segmento */}
                        <div className="w-full sm:w-48">
                            <Select
                                options={segmentOptions}
                                value={segmentOptions.find(opt => opt.value === filters.segment)}
                                onChange={(option) => filterBySegment(option.value)}
                                placeholder="Filtrar por segmento"
                                className="text-sm"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>

                        {/* Botones de acción */}
                        <div className="flex gap-2 flex-wrap">
                            {(filters.urgency || filters.segment) && (
                                <button
                                    onClick={clearFilters}
                                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Limpiar
                                </button>
                            )}

                            {/* Botón Enviar a todos HIGH */}
                            {customers.filter(c => c.urgency === 'high').length > 0 && (
                                <button
                                    onClick={handleSendBatchHigh}
                                    disabled={sendingBatch}
                                    className="px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 rounded-lg hover:from-red-700 hover:to-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    <FontAwesomeIcon icon={faRocket} className="mr-2" />
                                    {sendingBatch ? 'Enviando...' : `Enviar a todos HIGH (${customers.filter(c => c.urgency === 'high').length})`}
                                </button>
                            )}

                            <button
                                onClick={handleRefresh}
                                className="px-3 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
                            >
                                <FontAwesomeIcon icon={faSync} className="mr-2" />
                                Actualizar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Cliente
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Días Inactivo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Segmento
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Frecuencia
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Umbral Rec.
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Urgencia
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedCustomers.map((customer) => (
                            <tr
                                key={customer.clienteId}
                                className="hover:bg-gray-50 transition-colors"
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                                            {customer.nombre.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">
                                                {customer.nombre} {customer.apellido}
                                            </div>
                                            {customer.telefono && (
                                                <div className="text-xs text-gray-500">
                                                    {customer.telefono}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{customer.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-semibold text-gray-900">
                                        {customer.daysInactive} días
                                    </div>
                                    {customer.lastVisitDate && (
                                        <div className="text-xs text-gray-500">
                                            Última visita: {new Date(customer.lastVisitDate).toLocaleDateString()}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <SegmentBadge segment={customer.segment} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {customer.purchaseFrequency ? `${customer.purchaseFrequency.toFixed(1)} días` : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                                    {customer.recommendedThreshold} días
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <UrgencyBadge urgency={customer.urgency} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <button
                                        onClick={() => handleSendClick(customer)}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all"
                                    >
                                        <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                                        Enviar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                        Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, customers.length)} de {customers.length} clientes
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Anterior
                        </button>
                        <span className="px-4 py-2 text-sm font-medium text-gray-900">
                            Página {currentPage} de {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            )}

            {/* Modal de Envío */}
            {showSendModal && selectedClient && (
                <SendWinbackModal
                    cliente={selectedClient}
                    onClose={() => {
                        setShowSendModal(false);
                        setSelectedClient(null);
                    }}
                    onSuccess={handleSendSuccess}
                />
            )}
        </div>
    );
};

export default InactiveCustomersTable;

